import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import TableV2 from '@components/Tables/TableV2';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '@constants';
import { formatToDecimalPlaces } from '@helpers';

const FleetVesselsTable = ({ vessels, height, fileName }) => {
  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: 'vessel',
        label: 'Vessel Name',
        options: {
          sort: true,
          sortThirdClickReset: true,
          setCellProps: () => ({
            style: {
              boxShadow: '3px 0px 2px -1px rgb(0 0 0 / 20%)'
            }
          }),
          customBodyRender: (value) => (
            <ProfileLink profile={'vessel'} id={value.imo} name={value.name} />
          )
        }
      },
      {
        name: 'operator',
        label: 'Operator',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) =>
            value.id ? (
              <ProfileLink profile={'operator'} id={value.id} name={value.name} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {value.name || '-'}
              </Typography>
            )
        }
      },
      {
        name: 'destinationPortName',
        label: 'Next Port',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => <Typography variant="body2">{value || 'N/A'}</Typography>
        }
      },
      {
        name: 'eta',
        label: 'ETA',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => <Typography variant="body2">{value || 'N/A'}</Typography>
        }
      },
      {
        name: 'draught',
        label: 'Draught',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => <Typography variant="body2">{value || 'N/A'}</Typography>
        }
      }
    ];

    return baseColumns;
  }, []);

  const vesselTableData = useMemo(() => {
    if (!vessels?.length) return [];

    return vessels.map((vessel) => ({
      vessel: {
        name: vessel.vesselName,
        imo: vessel.imo || vessel.vesselImo
      },
      operator: {
        name: vessel.operatorShortName || '-',
        id: vessel.operatorId
      },
      destinationPortName: vessel.destinationPortName
        ? `${vessel.destinationPortName}${
            vessel.destinationCountryName ? `, ${vessel.destinationCountryName}` : ''
          }`
        : vessel.destination || '-',
      eta: vessel.eta ? moment(vessel.eta).format(DATE_TIME_FORMAT) : '-',
      draught:
        vessel.draught && vessel.vesselDraught && vessel.percentDraught
          ? `${formatToDecimalPlaces(vessel.draught, 1)} / ${formatToDecimalPlaces(
              vessel.vesselDraught,
              1
            )} (${formatToDecimalPlaces(vessel.percentDraught, 1)}%)`
          : vessel.draught
          ? `${formatToDecimalPlaces(vessel.draught, 1)}m`
          : '-'
    }));
  }, [vessels]);

  const options = useMemo(
    () => ({
      selectableRows: 'none',
      responsive: 'standard',
      filter: false,
      search: false,
      download: false,
      print: false,
      viewColumns: false,
      elevation: 0,
      pagination: true,
      rowsPerPageOptions: [5, 10, 15],
      tableBodyMaxHeight: height || '400px',
      textLabels: {
        body: {
          noMatch: 'No vessels in this fleet'
        }
      },
      setTableProps: () => ({
        size: 'small'
      }),
      downloadOptions: {
        filename: fileName || 'fleet-vessels'
      }
    }),
    [height, fileName]
  );

  return (
    <TableV2
      title={<Typography variant="h6">Vessels</Typography>}
      data={vesselTableData}
      columns={columns}
      options={options}
    />
  );
};

FleetVesselsTable.propTypes = {
  vessels: PropTypes.arrayOf(
    PropTypes.shape({
      imo: PropTypes.number.isRequired,
      vesselImo: PropTypes.number,
      vesselName: PropTypes.string.isRequired,
      operatorShortName: PropTypes.string,
      operatorId: PropTypes.number,
      destination: PropTypes.string,
      destinationPortName: PropTypes.string,
      destinationCountryName: PropTypes.string,
      destinationPortId: PropTypes.number,
      destinationCountryId: PropTypes.number,
      eta: PropTypes.string,
      draught: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vesselDraught: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      percentDraught: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fleetId: PropTypes.string,
      fleetName: PropTypes.string,
      fleetColor: PropTypes.string
    })
  ).isRequired,
  height: PropTypes.string,
  fileName: PropTypes.string
};

FleetVesselsTable.defaultProps = {
  height: '400px'
};

export default FleetVesselsTable;
